/** FE helpers mirroring BE VirtualOperatorRules. */
export interface VirtualOperatorProviderRef {
    isVirtualOperator?: boolean;
    IsVirtualOperator?: boolean;
    virtualOperatorId?: number | null;
    VirtualOperatorId?: number | null;
    virtualOperatorName?: string;
    VirtualOperatorName?: string;
    name?: string;
    Name?: string;
    enterpriseUnifiedNumber?: string;
    EnterpriseUnifiedNumber?: string;
}

export default class VirtualOperatorRules {
    public static isVirtualOperator(provider: VirtualOperatorProviderRef | null | undefined): boolean {
        if (!provider) {
            return false;
        }
        return !!(provider.isVirtualOperator || provider.IsVirtualOperator);
    }

    public static virtualOperatorId(provider: VirtualOperatorProviderRef | null | undefined): number | null {
        if (!provider) {
            return null;
        }
        const raw = provider.virtualOperatorId != null ? provider.virtualOperatorId : provider.VirtualOperatorId;
        if (raw === null || raw === undefined) {
            return null;
        }
        return Number(raw);
    }

    public static virtualOperatorName(provider: VirtualOperatorProviderRef | null | undefined): string {
        if (!provider) {
            return '';
        }
        return (provider.virtualOperatorName || provider.VirtualOperatorName || '').trim();
    }

    public static providerName(provider: VirtualOperatorProviderRef | null | undefined): string {
        if (!provider) {
            return '';
        }
        return provider.name || provider.Name || '';
    }

    public static providerEun(provider: VirtualOperatorProviderRef | null | undefined): string {
        if (!provider) {
            return '';
        }
        return provider.enterpriseUnifiedNumber || provider.EnterpriseUnifiedNumber || '';
    }

    public static canEditVirtualOperator(providerRequestStatus: number, role: string): boolean {
        if (providerRequestStatus !== 1 || !role) {
            return false;
        }
        const norm = String(role).toLowerCase().replace(/\s+/g, ' ').trim();
        return norm === 'platform admin' || norm === 'super admin';
    }

    public static applyPrivateClientFields(
        target: any,
        provider: VirtualOperatorProviderRef | null | undefined,
        lockValidationType: boolean,
    ): void {
        if (!VirtualOperatorRules.isVirtualOperator(provider)) {
            return;
        }
        target.customerType = 3;
        target.clientName = VirtualOperatorRules.providerName(provider);
        target.enterpriseUnifiedNumber = VirtualOperatorRules.providerEun(provider);
        if (lockValidationType) {
            target.verificationType = 0;
        }
    }

    public static connectivityOperators(
        provider: VirtualOperatorProviderRef | null | undefined,
        allOperatorNames: string[],
    ): { locked: boolean; names: string[] } {
        const voName = VirtualOperatorRules.virtualOperatorName(provider);
        if (VirtualOperatorRules.isVirtualOperator(provider) && voName) {
            return { locked: true, names: [voName] };
        }
        return { locked: false, names: allOperatorNames };
    }

    public static applyAutoConnectivityOperators(
        provider: VirtualOperatorProviderRef | null | undefined,
        isAutoActivation: boolean | string | null | undefined,
    ): string[] | null {
        const auto = isAutoActivation === true || isAutoActivation === 'true';
        const voName = VirtualOperatorRules.virtualOperatorName(provider);
        if (!auto || !VirtualOperatorRules.isVirtualOperator(provider) || !voName) {
            return null;
        }
        return [voName];
    }

    public static filterOperatorOptionsById<T extends { id?: number; Id?: number; name?: string; Name?: string }>(
        provider: VirtualOperatorProviderRef | null | undefined,
        operatorOptions: T[],
    ): { options: T[]; locked: boolean; operatorId: number | null } {
        const isVo = VirtualOperatorRules.isVirtualOperator(provider);
        const voId = VirtualOperatorRules.virtualOperatorId(provider);
        if (!isVo || voId === null) {
            return { options: operatorOptions, locked: false, operatorId: null };
        }
        const opId = (o: T) => Number(o.id != null ? o.id : o.Id);
        const opName = (o: T) => (o.name != null && o.name !== '' ? o.name : o.Name) || '';
        const match = operatorOptions.find((o) => opId(o) === voId);
        const operatorName = (match ? opName(match) : '') || VirtualOperatorRules.virtualOperatorName(provider);
        const options = operatorName
            ? [{ id: voId, name: operatorName } as T]
            : operatorOptions.filter((o) => opId(o) === voId);
        return { options, locked: true, operatorId: voId };
    }
}
